/**
 * Admin Console AuthContext — direct login (no OAuth redirect).
 * Authenticates via IC's /auth/login/ endpoint and stores tokens locally.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from '../api/axios';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');

  const hasToken = !!sessionStorage.getItem('access_token');

  const fetchUser = useCallback(async (force = false) => {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      setAuthChecked(true);
      return;
    }

    // Try to fetch user profile from API, but don't fail if unavailable
    // (user data may already be set from login response)
    try {
      const response = await axios.get('/auth/me/');
      const res = response?.data?.data;
      const userData = res?.user || response?.data;
      sessionStorage.setItem('user_role', userData?.role);
      setUser(userData);
    } catch (err) {
      // If API is unavailable but we have a stored role, keep the session alive
      const storedRole = sessionStorage.getItem('user_role');
      if (err.response?.status === 401 || !storedRole) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user_role');
        setUser(null);
      }
      // If we can't reach the API but have stored data, stay logged in
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/auth/logout/');
    } catch (error) {
      // Continue with frontend logout
    }

    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('platform_role');
    sessionStorage.removeItem('admin_console_user');
    localStorage.setItem('logout', Date.now().toString());
    setUser(null);
  }, []);

  // Initialize on mount — restore user from sessionStorage if available
  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    const storedUser = sessionStorage.getItem('admin_console_user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        // Invalid stored user — clear session
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('admin_console_user');
        sessionStorage.removeItem('platform_role');
        setUser(null);
      }
    }
    // No API call needed — we always use stored user data from login response
    setLoading(false);
    setAuthChecked(true);
  }, []);

  // Cross-tab logout
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout' && e.newValue) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user_role');
        sessionStorage.removeItem('platform_role');
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Derive platform_role from user or sessionStorage
  const platformRole = user?.platform_role || sessionStorage.getItem('platform_role') || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        logout,
        fetchUser,
        loading,
        authChecked,
        hasToken: !!sessionStorage.getItem('access_token'),
        currentPath,
        setCurrentPath,
        platformRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
